/**
 * Roles action hooks for document state management
 *
 * These hooks call direct named methods on the Zustand DocumentStore,
 * replacing the previous dispatch(actionCreator(...)) pattern.
 *
 * The direct store methods handle:
 * 1. Injecting currentUser into actions
 * 2. Applying mutations via withHistoryUpdate
 * 3. Notifying action listeners (ActionRecordService, DocumentSaveService, PatchHandlerService)
 */
import type {DocumentPermissionsData, IProjectInviteData, PermissionType} from "@buildwithflux/models";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import type {DocumentStore} from "modules/document/state/types";

/**
 * Hook to get the setRole action
 *
 * Calls the direct setRole method on the Zustand store.
 * This action adds or updates a user role/permission on the document.
 *
 * @returns A function to set a role on the document
 */
export function useSetRole(): (role: DocumentPermissionsData) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (role: DocumentPermissionsData) => {
            documentStore.getState().setRole(role);
        },
        [documentStore],
    );
}

/**
 * Hook to get the removeRole action
 *
 * Calls the direct removeRole method on the Zustand store.
 * This action removes a user role/permission from the document.
 *
 * @returns A function to remove a role from the document
 */
export function useRemoveRole(): (roleUid: string) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (roleUid: string) => {
            documentStore.getState().removeRole(roleUid);
        },
        [documentStore],
    );
}

/**
 * Hook to get the setProjectInvite action
 *
 * Calls the direct setProjectInvite method on the Zustand store.
 * This action adds or updates a project invite on the document.
 *
 * @returns A function to set a project invite on the document
 */
export function useSetProjectInvite(): (invite: IProjectInviteData) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (invite: IProjectInviteData) => {
            documentStore.getState().setProjectInvite(invite);
        },
        [documentStore],
    );
}

/**
 * Hook to get the removeProjectInvite action
 *
 * Calls the direct removeProjectInvite method on the Zustand store.
 * This action removes a project invite from the document.
 *
 * @returns A function to remove a project invite from the document
 */
export function useRemoveProjectInvite(): (invite: IProjectInviteData) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (invite: IProjectInviteData) => {
            documentStore.getState().removeProjectInvite(invite);
        },
        [documentStore],
    );
}

/**
 * Hook to get the setDocumentAsPrivate action
 *
 * Calls the direct setDocumentAsPrivate method on the Zustand store.
 * This action sets the document as private (removes public/anon access).
 *
 * @returns A function to set the document as private
 */
export function useSetDocumentAsPrivate(): () => void {
    const {documentStore} = useFluxServices();

    return useCallback(() => {
        documentStore.getState().setDocumentAsPrivate();
    }, [documentStore]);
}

/**
 * Hook to get the setOrganizationMemberPermissionType action
 *
 * Calls the direct setOrganizationMemberPermissionType method on the Zustand store.
 * This action sets the permission type for organization members on the document.
 *
 * @returns A function to set the organization member permission type
 */
export function useSetOrganizationMemberPermissionType(): (permissionType: PermissionType) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (permissionType: PermissionType) => {
            documentStore.getState().setOrganizationMemberPermissionType(permissionType);
        },
        [documentStore],
    );
}

/**
 * Hook to get the setEnterpriseMemberPermissionType action
 *
 * Calls the direct setEnterpriseMemberPermissionType method on the Zustand store.
 * This action sets the permission type for enterprise members on the document.
 *
 * @returns A function to set the enterprise member permission type
 */
export function useSetEnterpriseMemberPermissionType(): (permissionType: PermissionType) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (permissionType: PermissionType) => {
            documentStore.getState().setEnterpriseMemberPermissionType(permissionType);
        },
        [documentStore],
    );
}

/**
 * Factory function to create roles actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to perform roles actions.
 *
 * @param documentStore - The DocumentStore from FluxServices
 * @returns An object with roles action methods
 */
export function createRolesActions(documentStore: DocumentStore) {
    return {
        setRole: (role: DocumentPermissionsData) => {
            documentStore.getState().setRole(role);
        },
        removeRole: (roleUid: string) => {
            documentStore.getState().removeRole(roleUid);
        },
        setProjectInvite: (invite: IProjectInviteData) => {
            documentStore.getState().setProjectInvite(invite);
        },
        removeProjectInvite: (invite: IProjectInviteData) => {
            documentStore.getState().removeProjectInvite(invite);
        },
        setDocumentAsPrivate: () => {
            documentStore.getState().setDocumentAsPrivate();
        },
        setOrganizationMemberPermissionType: (permissionType: PermissionType) => {
            documentStore.getState().setOrganizationMemberPermissionType(permissionType);
        },
        setEnterpriseMemberPermissionType: (permissionType: PermissionType) => {
            documentStore.getState().setEnterpriseMemberPermissionType(permissionType);
        },
    };
}
